import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNotification } from './NotificationContext';
import InputMask from 'react-input-mask';
import { sendResetSms } from './api.js';
import { useHookState } from './NotificationApiHook';

const ResetPasswordModal = ({ open, onClose, onPwdChange }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [smsCode, setSmsCode] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);
  const theme = useTheme();
  const { openNotification } = useNotification();
  const [smsSent, setSmsSent] = useState(false);
  const { notificationState } = useHookState();
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    const handleLoaded = () => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute('6LfA2xUpAAAAAO0EiF0xvzuaq7yjGZg_UFCR64cB', { action: 'registration' })
          .then((token) => {
            console.log(token);
            setRecaptchaValue(token);
          });
      });
    };

    // Add reCaptcha script
    const script = document.createElement('script');
    script.src =
      'https://www.google.com/recaptcha/api.js?render=6LfA2xUpAAAAAO0EiF0xvzuaq7yjGZg_UFCR64cB';
    script.addEventListener('load', handleLoaded);
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove the script when the component is unmounted
      document.body.removeChild(script);
    };
  }, [, refreshToken]);

  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleRepeatPasswordChange = (e) => {
    setRepeatPassword(e.target.value);
  };

  const handleSmsCodeChange = (e) => {
    setSmsCode(e.target.value);
  };

  const handleSendSMS = async () => {
    setLoading(true);
    try {
      //refresh to get new token on each button click
      setRefreshToken(refreshToken + 1);
      if (!recaptchaValue) {
        openNotification('Подтвердите, что вы не робот', 'error');
        return;
      }

      const responseData = await sendResetSms(phone, notificationState, recaptchaValue);
      console.log(responseData);
      if (responseData) {
        const token = responseData.token;
        sessionStorage.setItem('reset_token', token);
        setSmsSent(true);
      }
    } finally {
      setLoading(false);
    }
  };
  const handlePwdChange = () => {
    if (password.length < 8) {
      openNotification('Пароль должен содержать минимум 8 символов', 'error');
      return;
    }

    if (password === repeatPassword) {
      onPwdChange(password, smsCode);
    } else {
      setPasswordMatch(false);
      openNotification('Пароли не совпадают', 'error');
    }
  };

  const handleClose = () => {
    setName('');
    setPhone('');
    setPassword('');
    setRepeatPassword('');
    setPasswordMatch(true);
    setSmsSent(false);
    onClose();
  };

  return (
    <Dialog
      maxWidth={'sm'}
      open={open}
      onClose={handleClose}
      sx={{ flexDirection: 'column', m: 'auto' }}
    >
      <DialogTitle sx={{ textAlign: 'center', marginBottom: '10px' }}>Смена пароля</DialogTitle>
      <DialogContent sx={{ minWidth: 326 }}>
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          mt={0}
        >
          <Typography
            width={200}
            sx={{ color: 'grey', fontSize: '8px', textAlign: 'center' }}
          >
            This site is protected by reCAPTCHA and the Google <br></br>
            <a
              href='https://policies.google.com/privacy'
              target='_blank'
              rel='noopener noreferrer'
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href='https://policies.google.com/terms'
              target='_blank'
              rel='noopener noreferrer'
            >
              Terms of Service
            </a>{' '}
            apply.
          </Typography>
        </Box>
        {smsSent ? (
          <>
            <Box
              mt={2}
              display='flex'
              justifyContent='center'
              alignItems='center'
            >
              <InputMask
                mask='999999'
                maskChar={null}
                value={smsCode}
                onChange={handleSmsCodeChange}
              >
                {() => <TextField label='Код из СМС' />}
              </InputMask>
            </Box>
            <Box
              mt={2}
              display='flex'
              justifyContent='center'
              alignItems='center'
            >
              <TextField
                sx={{ width: '25ch' }}
                type={showPassword ? 'text' : 'password'}
                label='Новый пароль'
                value={password}
                onChange={handlePasswordChange}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handleTogglePassword}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Box>
            <Box
              mt={2}
              display='flex'
              justifyContent='center'
              alignItems='center'
            >
              <TextField
                sx={{ width: '25ch' }}
                type={showPassword ? 'text' : 'password'}
                label='Повторите пароль'
                value={repeatPassword}
                onChange={handleRepeatPasswordChange}
                error={!passwordMatch}
                helperText={!passwordMatch && 'Пароли не совпадают'}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handleTogglePassword}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <Box
              mt={2}
              display='flex'
              justifyContent='center'
              alignItems='center'
            >
              <InputMask
                mask='+79999999999'
                maskChar='_'
                value={phone}
                onChange={handlePhoneChange}
              >
                {() => <TextField label='Телефон' />}
              </InputMask>
            </Box>

            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              mt={2}
            >
              {!loading ? (
                <Button
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderRadius: '0px',
                  }}
                  color='primary'
                  onClick={handleSendSMS}
                >
                  Отправить СМС
                </Button>
              ) : (
                <LoadingButton
                  color='primary'
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderRadius: '0px',
                    paddingTop: 4,
                    paddingBottom: 3,
                    width: '180px',
                    '&.Mui-disabled': {
                      backgroundColor: '#fff',
                      color: theme.palette.primary.contrastText,
                    },
                    '&:hover': {
                      backgroundColor: '#8e80d7',
                    },
                  }}
                  disabled
                  variant='contained'
                  loading={loading}
                ></LoadingButton>
              )}
            </Box>
          </>
        )}
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          mt={2}
        >
          <Typography
            width={200}
            sx={{ color: 'grey', fontSize: '10px', textAlign: 'center' }}
          >
            Передавая номер телефона, вы даете согласие на обработку{' '}
            <Link
              to='/privacy-policy'
              style={{
                textAlign: 'center',
                textDecoration: 'underline',
                color: '#2B476A',
                marginBottom: 14,
                marginTop: 0,
              }}
            >
              своих персональных данных
            </Link>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', textAlign: 'center' }}>
        <Button onClick={handleClose}>Закрыть</Button>
        {!loading ? (
          <Button
            color='primary'
            onClick={handlePwdChange}
            disabled={!smsSent || !phone || !password || !smsCode || !repeatPassword}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: '0px',
              '&.Mui-disabled': {
                backgroundColor: '#4b4b4b',
                color: theme.palette.primary.contrastText,
              },
            }}
          >
            Сменить пароль
          </Button>
        ) : (
          <LoadingButton
            color='primary'
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: '0px',
              paddingBottom: 2,
              width: '180px',
              '&.Mui-disabled': {
                backgroundColor: '#fff',
                color: theme.palette.primary.contrastText,
              },
              '&:hover': {
                backgroundColor: '#8e80d7',
              },
            }}
            disabled
            variant='contained'
            loading={loading}
          ></LoadingButton>
        )}
      </DialogActions>
      <div
        className='g-recaptcha'
        data-sitekey='6LfA2xUpAAAAAO0EiF0xvzuaq7yjGZg_UFCR64cB'
        data-size='invisible'
      ></div>
    </Dialog>
  );
};

export default ResetPasswordModal;
